import { useState, useCallback } from "react";
import { getEvaluationByHostId, getEvaluationByResto, getEvaluationRestoById, getEvaluationByRestos } from "../services/evaluationService";
import { useGlobalContext } from "../context/useGlobalContext"
import { useUser } from "./useUser";
import { getTenantRestoEvaluations } from "../services/tenantOwnerService";
import { getTenantId } from "../utils/tenant";
import { useResto } from "./useResto";

const toEvaluationArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.evaluations)) return payload.evaluations;
    return [];
};

const normalizeTenantRestoList = (payload) => {
    const list = toEvaluationArray(payload);
    return list.filter((item) => item && (item.restoId || item._id || item.place_id));
};

const getTenantRestoEvaluationsForRestaurant = (payload, restoId) => {
    const list = normalizeTenantRestoList(payload);
    if (!list.length) return [];

    const target = restoId
        ? list.find((item) =>
            [
                item?.restoId,
                item?.restaurantId,
                item?._id,
                item?.resto?._id,
                item?.place_id
            ]
                .filter(Boolean)
                .map(String)
                .includes(String(restoId))
        ) || list[0]
        : list[0];

    const averages = Array.isArray(target?.averages) ? target.averages : [];
    return averages;
};

export const useEvaluation = () => {
    const { restoEvaluation, setRestoEvaluation } = useGlobalContext();
    const { isOwner, type } = useUser();
    const { resto } = useResto();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const tenantId = getTenantId();
    const shouldUseTenantEvaluations = Boolean(tenantId) && (isOwner || type === "resto");

    const getEvalByResto = useCallback(async (restoIdOverride) => {
        try {
            setLoading(true);
            setError(null);
            const currentRestoId =
                restoIdOverride ||
                resto?._id ||
                resto?.id ||
                resto?.place_id ||
                resto?.restoId;
            const res = shouldUseTenantEvaluations
                ? await getTenantRestoEvaluations(getTenantId())
                : currentRestoId
                    ? await getEvaluationRestoById(currentRestoId)
                    : await getEvaluationByResto();
            const list = shouldUseTenantEvaluations
                ? getTenantRestoEvaluationsForRestaurant(res, currentRestoId)
                : toEvaluationArray(res);
            setRestoEvaluation(list);
            return list;
        } catch (err) {
            console.error("Error en getEvalByResto:", err);
            setError(err.message || "Error al cargar evaluaciones");
            setRestoEvaluation([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [resto, setRestoEvaluation, shouldUseTenantEvaluations]);

    const getEvalByHost = useCallback(async (id) => {
        if (!id) {
            setError("ID de host es requerido");
            return [];
        }
        try {
            setLoading(true);
            setError(null);
            const res = await getEvaluationByHostId(id);
            const list = toEvaluationArray(res);
            setRestoEvaluation(list);
            return list;
        } catch (err) {
            console.error("Error en getEvalByHost:", err);
            setError(err.message || "Error al cargar evaluaciones del host");
            setRestoEvaluation([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [setRestoEvaluation]);

    const getEvalRestoById = useCallback(async (id) => {
        if (!id) {
            setError("ID de resto es requerido");
            return [];
        }
        try {
            setLoading(true);
            setError(null);
            const res = shouldUseTenantEvaluations
                ? await getTenantRestoEvaluations(getTenantId())
                : await getEvaluationRestoById(id);
            const list = shouldUseTenantEvaluations
                ? getTenantRestoEvaluationsForRestaurant(res, id)
                : toEvaluationArray(res);
            setRestoEvaluation(list);
            return list;
        } catch (err) {
            console.error("Error en getEvalRestoById:", err);
            setError(err.message || "Error al cargar evaluaciones del resto");
            setRestoEvaluation([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [setRestoEvaluation, shouldUseTenantEvaluations]);

    const getEvalByRestos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = shouldUseTenantEvaluations
                ? await getTenantRestoEvaluations(getTenantId())
                : await getEvaluationByRestos();
            const list = toEvaluationArray(res);
            setRestoEvaluation(list);
            return list;
        } catch (err) {
            console.error("Error en getEvalByRestos:", err);
            setError(err.message || "Error al cargar evaluaciones");
            setRestoEvaluation([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [setRestoEvaluation, shouldUseTenantEvaluations]);

    return {
        restoEvaluation,
        loading,
        error,
        getEvalByResto,
        getEvalByHost,
        getEvalRestoById,
        getEvalByRestos
    }
}
